import { isDefined } from "@clipboard-health/util-ts";
import {
  NURSE_QUALIFICATION,
  NURSE_QUALIFICATIONS,
} from "@src/appV2/Shifts/constants/qualifications";

export const QUALIFICATION_WITHOUT_CHARGE_TOOLTIP =
  "This professional type is not included in your contract yet. Please contact support to update your contract.";

export const NO_CONTRACTED_QUALIFICATIONS_MESSAGE =
  "No professional types are included in your contract yet. Please contact support to update your contract.";

interface QualificationChargeStatus {
  name: string;
  contracted?: boolean;
}

/**
 * Returns the set of qualification names that should be displayed on calendar
 * surfaces. Callers use `available.has(name)` to decide whether to show a
 * qualification, and `!available.has(name)` to decide whether to show the
 * "not in contract" tooltip / disabled treatment.
 *
 * Inclusion rules:
 * - A qualification is included when it has a configured charge rate
 *   (`contracted !== false`).
 * - NURSE is included only when BOTH RN and LVN are present in the input AND
 *   have charge rates. NURSE itself doesn't carry a charge rate — pricing
 *   falls through to RN/LVN — so its own `contracted` flag is not checked.
 */
export function getQualificationsAvailableToDisplay(
  qualifications: readonly QualificationChargeStatus[]
): Set<string> {
  const available = new Set<string>();
  const qualificationByName = new Map(qualifications.map((q) => [q.name, q]));

  for (const qualification of qualifications) {
    if (qualification.name === NURSE_QUALIFICATION) {
      continue;
    }
    if (qualification.contracted !== false) {
      available.add(qualification.name);
    }
  }

  if (qualificationByName.has(NURSE_QUALIFICATION)) {
    const nurseHasCharge = NURSE_QUALIFICATIONS.every((name) => {
      const dependency = qualificationByName.get(name);
      return isDefined(dependency) && dependency.contracted !== false;
    });
    if (nurseHasCharge) {
      available.add(NURSE_QUALIFICATION);
    }
  }

  return available;
}
